import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, Platform, ActivityIndicator, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { DocumentPreviewModal } from '../components/documents/DocumentPreviewModal';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography, withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { usePermissions } from '../hooks/usePermissions';
import { useLocale } from '../contexts/LocaleContext';
import { DOCUMENT_ICONS, DOCUMENT_TYPES, DocumentLanguage, DocumentType, isDocumentType } from '../utils/documentTypes';

interface DocumentTemplate {
  _id: string;
  name: string;
  type: DocumentType;
  isDefault?: boolean;
  language?: DocumentLanguage;
  htmlContent?: string;
  logoUrl?: string;
  colors?: { primary?: string; secondary?: string; text?: string; background?: string };
}

/** Choose a document first. Its standard layout works immediately; custom versions are optional. */
export const QuotationTemplatesListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const { navigateTo, activeTab, setActiveTab, showNotification, routeParams } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  const canEdit = hasPermissionAtLeast('general.manageOrders', 'edit');
  const businessId = useAppStore((s) => String(s.currentBusiness?._id || (s.currentBusiness as any)?.id || ''));
  const [selection, setSelection] = useState<DocumentType | null>(isDocumentType(routeParams?.documentType) ? routeParams.documentType : null);
  const [search, setSearch] = useState('');
  const [contentWidth, setContentWidth] = useState(0);
  const [data, setData] = useState<{ businessId: string; templates: DocumentTemplate[]; loading: boolean; error: string | null }>({ businessId, templates: [], loading: true, error: null });
  const [savingDefault, setSavingDefault] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [preview, setPreview] = useState({ visible: false, loading: false, html: '', title: '', error: '' });
  const listRequest = useRef(0);
  const previewRequest = useRef(0);
  const defaultRequest = useRef(0);
  const defaultBusy = useRef(false);
  const activeBusiness = useRef(businessId);
  const previewTarget = useRef<{ type: DocumentType; template?: DocumentTemplate } | null>(null);
  const notify = useRef(showNotification);
  const translate = useRef(t);
  activeBusiness.current = businessId;
  notify.current = showNotification;
  translate.current = t;

  const loadTemplates = useCallback(async () => {
    const request = ++listRequest.current;
    setData({ businessId, templates: [], loading: true, error: null });
    try {
      if (!businessId) throw new Error(translate.current('documents.hub.loadError'));
      const response = await apiService.getQuotationTemplates();
      if (request !== listRequest.current || businessId !== activeBusiness.current) return;
      if (response.statusCode !== 1000 || !Array.isArray(response.data)) throw new Error(translate.current('documents.hub.loadError'));
      setData({ businessId, templates: response.data.filter((x) => isDocumentType(x.type)), loading: false, error: null });
    } catch {
      if (request === listRequest.current && businessId === activeBusiness.current) {
        setData({ businessId, templates: [], loading: false, error: translate.current('documents.hub.loadError') });
      }
    }
  }, [businessId]);

  useEffect(() => {
    setSelection(isDocumentType(routeParams?.documentType) ? routeParams.documentType : null);
    setSearch('');
    setSavingDefault(null);
    defaultBusy.current = false;
    defaultRequest.current++;
    setActionError(null);
    previewRequest.current++;
    setPreview((p) => ({ ...p, visible: false }));
    void loadTemplates();
    return () => { listRequest.current++; previewRequest.current++; defaultRequest.current++; };
  }, [businessId, loadTemplates]);

  useEffect(() => {
    if (isDocumentType(routeParams?.documentType)) setSelection(routeParams.documentType);
  }, [routeParams?.documentType]);

  const isCurrent = data.businessId === businessId;
  const templates = isCurrent ? data.templates : [];
  const loading = !isCurrent || data.loading;
  const loadError = isCurrent ? data.error : null;
  const byType = useMemo(() => DOCUMENT_TYPES.reduce((result, type) => {
    result[type] = templates.filter((x) => x.type === type);
    return result;
  }, {} as Record<DocumentType, DocumentTemplate[]>), [templates]);
  const selectedTemplates = selection ? byType[selection] : [];
  const defaultTemplate = selectedTemplates.find((x) => x.isDefault);
  const query = search.trim().toLocaleLowerCase();
  const shown = selectedTemplates.filter((x) => !query || x.name.toLocaleLowerCase().includes(query));
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const chooseType = (type: DocumentType | null) => {
    setSelection(type);
    setSearch('');
    setActionError(null);
  };
  const openEditor = (type: DocumentType, template?: DocumentTemplate, copy = false) => {
    if (!canEdit) return;
    navigateTo('quotation-template-edit', copy ? undefined : template?._id, { documentType: type, ...(copy && template ? { duplicateTemplateId: template._id } : {}) });
  };
  const closePreview = () => {
    previewRequest.current++;
    setPreview((p) => ({ ...p, visible: false }));
  };
  const openPreview = async (type: DocumentType, template?: DocumentTemplate) => {
    const request = ++previewRequest.current;
    const scope = businessId;
    previewTarget.current = { type, template };
    const title = t('documents.hub.previewTitle', { name: `${t(`documents.type.${type}` as any)} · ${template?.name || t('documents.hub.standardTitle')}` });
    setPreview({ visible: true, loading: true, html: '', title, error: '' });
    try {
      let source = template;
      let html = source?.htmlContent;
      if (source && !html) {
        const response = await apiService.getQuotationTemplateById(source._id);
        if (request !== previewRequest.current || scope !== activeBusiness.current) return;
        if (response.statusCode !== 1000 || !response.data?.htmlContent) throw new Error(t('quotations.templateEditor.previewError'));
        source = response.data;
        html = source?.htmlContent;
      } else if (!source) {
        const response = await apiService.getDocumentDefaultHtml();
        if (request !== previewRequest.current || scope !== activeBusiness.current) return;
        if (response.statusCode !== 1000 || !response.data?.html) throw new Error(t('quotations.templateEditor.previewError'));
        html = response.data.html;
      }
      if (!html) throw new Error(t('quotations.templateEditor.previewError'));
      const response = await apiService.previewQuotationTemplate({ htmlContent: html, type, language: source?.language || 'auto', logoUrl: source?.logoUrl, colors: source?.colors });
      if (request !== previewRequest.current || scope !== activeBusiness.current) return;
      if (response.statusCode !== 1000 || !response.data?.html) throw new Error(response.errors?.[0]?.message || t('quotations.templateEditor.previewError'));
      setPreview({ visible: true, loading: false, html: response.data.html, title, error: '' });
    } catch (error: any) {
      if (request === previewRequest.current && scope === activeBusiness.current) setPreview({ visible: true, loading: false, html: '', title, error: error.message || t('quotations.templateEditor.previewError') });
    }
  };
  const useDefault = async (type: DocumentType, template?: DocumentTemplate) => {
    if (!canEdit || defaultBusy.current) return;
    defaultBusy.current = true;
    const request = ++defaultRequest.current;
    const scope = businessId;
    setSavingDefault(template?._id || 'standard');
    setActionError(null);
    try {
      const updates = template ? [apiService.updateQuotationTemplate(template._id, { isDefault: true })]
        : byType[type].filter((x) => x.isDefault).map((x) => apiService.updateQuotationTemplate(x._id, { isDefault: false }));
      const responses = await Promise.all(updates);
      if (request !== defaultRequest.current || scope !== activeBusiness.current) return;
      if (responses.some((r) => r.statusCode !== 1000)) throw new Error(t('documents.hub.defaultSaveError'));
      setData((current) => current.businessId !== scope ? current : { ...current, templates: current.templates.map((x) => x.type === type ? { ...x, isDefault: x._id === template?._id } : x) });
      notify.current('success', t('common.success'), t('documents.hub.defaultSaved'));
    } catch {
      if (request === defaultRequest.current && scope === activeBusiness.current) {
        setActionError(t('documents.hub.defaultSaveError'));
        // A timed-out update may have committed. Refresh before showing the active default again.
        await loadTemplates();
      }
    } finally {
      if (request === defaultRequest.current && scope === activeBusiness.current) {
        defaultBusy.current = false;
        setSavingDefault(null);
      }
    }
  };

  const action = (label: string, icon: string, onPress: () => void, options: { primary?: boolean; busy?: boolean; disabled?: boolean } = {}) => (
    <TouchableOpacity accessibilityRole="button" accessibilityLabel={label} disabled={!!options.disabled || !!options.busy} style={[styles.action, { flexDirection: rtl.rowDirection }, options.primary && styles.actionPrimary, (options.disabled || options.busy) && styles.disabled]} onPress={onPress}>
      {options.busy ? <ActivityIndicator size="small" color={(options.primary ? colors.white : colors.primary) as string} /> : <Ionicons name={icon as any} size={17} color={(options.primary ? colors.white : colors.primary) as string} />}
      <Text style={[styles.actionText, options.primary && styles.actionPrimaryText]}>{label}</Text>
    </TouchableOpacity>
  );
  const defaultBadge = () => (
    <View style={[styles.badge, { flexDirection: rtl.rowDirection }]}>
      <Ionicons name="checkmark-circle" size={13} color={colors.primary as string} />
      <Text style={styles.badgeText}>{t('documents.hub.defaultBadge')}</Text>
    </View>
  );

  return (
    <ScreenLayout
      header={<Header showNotifications showMessages onNotificationPress={() => navigateTo('notifications')} onMessagePress={() => navigateTo('chat')} showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />}
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title={selection ? t(`documents.type.${selection}` as any) : t('documents.templatesTitle')} onBack={() => selection ? chooseType(null) : navigateTo('settings')} actions={selection && canEdit ? [{ icon: 'add', primary: true, onPress: () => openEditor(selection), label: t('documents.newTemplateAction'), accessibilityLabel: t('documents.newTemplateAction'), disabled: loading || !!loadError }] : []} />}
    >
      <View style={styles.container} onLayout={(event) => setContentWidth(event.nativeEvent.layout.width)}>
        {loading ? (
          <View style={styles.center}><ActivityIndicator size="large" color={colors.primary as string} /></View>
        ) : loadError ? (
          <View style={styles.center}>
            <Ionicons name="cloud-offline-outline" size={40} color={colors.text.secondary as string} />
            <Text style={styles.emptyTitle}>{loadError}</Text>
            {action(t('documents.hub.retry'), 'refresh-outline', () => void loadTemplates(), { primary: true })}
          </View>
        ) : (
          <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
            <View style={styles.content}>
              {!selection ? (
                <>
                  <View style={styles.introduction}>
                    <Text style={[styles.heading, { textAlign: rtl.textStart }]}>{t('documents.hub.chooseDocument')}</Text>
                    <Text style={[styles.description, { textAlign: rtl.textStart }]}>{t('documents.hub.intro')}</Text>
                  </View>
                  <View style={[styles.grid, { flexDirection: rtl.rowDirection }]}>
                    {DOCUMENT_TYPES.map((type) => {
                      const items = byType[type];
                      const current = items.find((x) => x.isDefault);
                      return (
                        <TouchableOpacity key={type} accessibilityRole="button" accessibilityLabel={t(`documents.type.${type}` as any)} onPress={() => chooseType(type)} activeOpacity={0.75} style={[styles.typeCard, { width: contentWidth >= 760 ? '48.5%' : '100%' }]}>
                          <View style={[styles.typeCardTop, { flexDirection: rtl.rowDirection }]}>
                            <View style={styles.typeIcon}><Ionicons name={DOCUMENT_ICONS[type] as any} size={24} color={colors.primary as string} /></View>
                            <View style={styles.flex}>
                              <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t(`documents.type.${type}` as any)}</Text>
                              <Text style={[styles.description, { textAlign: rtl.textStart }]}>{t(`documents.typeHint.${type}` as any)}</Text>
                            </View>
                            <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={19} color={colors.text.secondary as string} />
                          </View>
                          <View style={styles.typeCardBottom}>
                            <View style={[styles.defaultLine, { flexDirection: rtl.rowDirection }]}>
                              <Ionicons name="checkmark-circle-outline" size={15} color={colors.primary as string} />
                              <Text style={[styles.defaultName, { textAlign: rtl.textStart }]} numberOfLines={1}>{current?.name || t('documents.hub.standardTitle')}</Text>
                            </View>
                            <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{t('documents.hub.customCount', { count: items.length })}</Text>
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </>
              ) : (
                <>
                  <TouchableOpacity accessibilityRole="button" style={[styles.backLink, { flexDirection: rtl.rowDirection }]} onPress={() => chooseType(null)}>
                    <Ionicons name={rtl.mirrorIcon('arrow-back') as any} size={16} color={colors.primary as string} />
                    <Text style={styles.actionText}>{t('documents.hub.backToTypes')}</Text>
                  </TouchableOpacity>
                  <View style={styles.defaultSummary}>
                    <Text style={[styles.eyebrow, { textAlign: rtl.textStart }]}>{t('documents.hub.currentDefault')}</Text>
                    <Text style={[styles.heading, { textAlign: rtl.textStart }]}>{defaultTemplate?.name || t('documents.hub.standardTitle')}</Text>
                    <Text style={[styles.description, { textAlign: rtl.textStart }]}>{t('documents.hub.defaultHelp')}</Text>
                    <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
                      {action(t('documents.hub.preview'), 'eye-outline', () => void openPreview(selection, defaultTemplate))}
                      {canEdit && action(t('documents.hub.customize'), 'color-wand-outline', () => openEditor(selection, defaultTemplate), { primary: true, disabled: !!savingDefault })}
                    </View>
                    {!!actionError && <Text accessibilityRole="alert" style={[styles.error, { textAlign: rtl.textStart }]}>{actionError}</Text>}
                  </View>

                  <View style={styles.templateCard}>
                    <View style={[styles.templateHead, { flexDirection: rtl.rowDirection }]}>
                      <View style={styles.paperIcon}><Ionicons name="document-outline" size={25} color={colors.primary as string} /></View>
                      <View style={styles.flex}>
                        <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('documents.hub.standardTitle')}</Text>
                        <Text style={[styles.description, { textAlign: rtl.textStart }]}>{t('documents.hub.standardHint')}</Text>
                      </View>
                      {!defaultTemplate && defaultBadge()}
                    </View>
                    <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
                      {action(t('documents.hub.preview'), 'eye-outline', () => void openPreview(selection))}
                      {canEdit && action(t('documents.hub.customize'), 'color-wand-outline', () => openEditor(selection), { disabled: !!savingDefault })}
                      {canEdit && !!defaultTemplate && action(t('documents.hub.useDefault'), 'checkmark-circle-outline', () => void useDefault(selection), { busy: savingDefault === 'standard', disabled: !!savingDefault })}
                    </View>
                  </View>

                  <View style={styles.customSection}>
                    <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('documents.hub.customTitle')} · {selectedTemplates.length}</Text>
                    {selectedTemplates.length > 0 && (
                      <View style={[styles.searchBox, { flexDirection: rtl.rowDirection }]}>
                        <Ionicons name="search-outline" size={18} color={colors.text.secondary as string} />
                        <TextInput accessibilityLabel={t('documents.hub.searchPlaceholder')} placeholder={t('documents.hub.searchPlaceholder')} placeholderTextColor={colors.text.disabled as string} value={search} onChangeText={setSearch} style={[styles.searchInput, { textAlign: rtl.textStart }]} autoCapitalize="none" />
                        {!!search && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('documents.hub.clearSearch')} onPress={() => setSearch('')} style={styles.clearButton}><Ionicons name="close" size={18} color={colors.text.secondary as string} /></TouchableOpacity>}
                      </View>
                    )}
                    {selectedTemplates.length === 0 ? (
                      <View style={styles.emptyCustom}>
                        <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('documents.hub.customEmpty')}</Text>
                        <Text style={[styles.description, { textAlign: rtl.textStart }]}>{t('documents.hub.customEmptyHint')}</Text>
                        {canEdit && action(t('documents.newTemplateAction'), 'add-outline', () => openEditor(selection), { primary: true })}
                      </View>
                    ) : shown.length === 0 ? (
                      <View style={styles.emptyCustom}>
                        <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('documents.hub.noMatches')}</Text>
                        {action(t('documents.hub.clearSearch'), 'close-outline', () => setSearch(''))}
                      </View>
                    ) : shown.map((template) => (
                      <View key={template._id} style={styles.templateCard}>
                        <View style={[styles.templateHead, { flexDirection: rtl.rowDirection }]}>
                          <View style={[styles.swatch, { backgroundColor: template.colors?.primary || colors.primary }]}><Ionicons name={DOCUMENT_ICONS[selection] as any} size={22} color={colors.white as string} /></View>
                          <View style={styles.flex}>
                            <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{template.name}</Text>
                            <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{t('documents.hub.defaultLanguage', { language: t(`documents.lang.${template.language || 'auto'}` as any) })}</Text>
                          </View>
                          {template.isDefault && defaultBadge()}
                        </View>
                        <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
                          {action(t('documents.hub.preview'), 'eye-outline', () => void openPreview(selection, template))}
                          {canEdit && action(t('documents.hub.edit'), 'create-outline', () => openEditor(selection, template), { disabled: !!savingDefault })}
                          {canEdit && !template.isDefault && action(t('documents.hub.useDefault'), 'checkmark-circle-outline', () => void useDefault(selection, template), { busy: savingDefault === template._id, disabled: !!savingDefault })}
                          {canEdit && action(t('documents.hub.copy'), 'copy-outline', () => openEditor(selection, template, true), { disabled: !!savingDefault })}
                        </View>
                      </View>
                    ))}
                  </View>
                </>
              )}
              {!canEdit && <Text style={[styles.readOnly, { textAlign: rtl.textStart }]}>{t('documents.hub.readOnly')}</Text>}
            </View>
          </ScrollView>
        )}
      </View>
      <DocumentPreviewModal visible={preview.visible && isCurrent} html={preview.html} loading={preview.loading} title={preview.title} error={preview.error || undefined} onClose={closePreview} onRetry={() => { const target = previewTarget.current; if (target) void openPreview(target.type, target.template); }} />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  scrollContent: { flexGrow: 1, padding: spacing.lg, paddingBottom: Platform.OS === 'web' ? spacing.xl : 100 },
  content: { width: '100%', maxWidth: 1040, alignSelf: 'center', gap: spacing.lg },
  introduction: { gap: spacing.xs },
  heading: { color: colors.text.primary, fontSize: typography.fontSize.xl, fontWeight: '700' },
  description: { color: colors.text.secondary, fontSize: typography.fontSize.sm, lineHeight: 21 },
  grid: { flexWrap: 'wrap', gap: spacing.md, alignItems: 'stretch' },
  typeCard: { borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.lg, backgroundColor: colors.background.surface, overflow: 'hidden' },
  typeCardTop: { alignItems: 'center', gap: spacing.md, padding: spacing.lg, minHeight: 120 },
  typeIcon: { width: 48, height: 48, borderRadius: 14, backgroundColor: withAlpha(colors.primary, 0.08), alignItems: 'center', justifyContent: 'center' },
  typeCardBottom: { paddingHorizontal: spacing.lg, paddingVertical: spacing.md, borderTopWidth: 1, borderTopColor: colors.border, gap: 5 },
  defaultLine: { alignItems: 'center', gap: 7 },
  defaultName: { color: colors.text.primary, fontSize: typography.fontSize.sm, fontWeight: '600', flexShrink: 1 },
  cardTitle: { color: colors.text.primary, fontSize: typography.fontSize.base, fontWeight: '700' },
  meta: { color: colors.text.secondary, fontSize: typography.fontSize.xs, marginTop: 3 },
  flex: { flex: 1, minWidth: 0, gap: 3 },
  backLink: { alignItems: 'center', alignSelf: 'flex-start', gap: 7, minHeight: 44 },
  defaultSummary: { borderRadius: designTokens.borderRadius.lg, backgroundColor: withAlpha(colors.primary, 0.06), borderWidth: 1, borderColor: withAlpha(colors.primary, 0.24), padding: spacing.lg, gap: spacing.sm },
  eyebrow: { color: colors.primary, fontSize: typography.fontSize.xs, fontWeight: '700' },
  actions: { flexWrap: 'wrap', alignItems: 'center', gap: spacing.sm, marginTop: spacing.sm },
  action: { alignItems: 'center', justifyContent: 'center', gap: 7, paddingHorizontal: spacing.md, minHeight: 44, paddingVertical: spacing.sm, borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, backgroundColor: colors.background.surface },
  actionPrimary: { backgroundColor: colors.primary, borderColor: colors.primary },
  actionText: { color: colors.primary, fontSize: typography.fontSize.sm, fontWeight: '600', flexShrink: 1 },
  actionPrimaryText: { color: colors.white },
  disabled: { opacity: 0.5 },
  templateCard: { padding: spacing.lg, borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.lg, backgroundColor: colors.background.surface, gap: spacing.xs },
  templateHead: { alignItems: 'center', gap: spacing.md, flexWrap: 'wrap' },
  paperIcon: { width: 46, height: 56, borderWidth: 1, borderColor: colors.border, borderRadius: 7, justifyContent: 'center', alignItems: 'center', backgroundColor: withAlpha(colors.primary, 0.04) },
  swatch: { width: 46, height: 46, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  badge: { alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: 5, backgroundColor: withAlpha(colors.primary, 0.08), borderRadius: 999 },
  badgeText: { color: colors.primary, fontSize: typography.fontSize.xs, fontWeight: '600' },
  customSection: { gap: spacing.md },
  sectionTitle: { color: colors.text.primary, fontSize: typography.fontSize.lg, fontWeight: '700' },
  searchBox: { alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, minHeight: 48, backgroundColor: colors.background.surface, borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md },
  searchInput: { flex: 1, color: colors.text.primary, fontSize: typography.fontSize.sm, paddingVertical: spacing.sm, minWidth: 0 },
  clearButton: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  emptyCustom: { padding: spacing.lg, borderWidth: 1, borderColor: colors.border, borderStyle: 'dashed', borderRadius: designTokens.borderRadius.lg, gap: spacing.sm, alignItems: 'stretch' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, gap: spacing.lg },
  emptyTitle: { color: colors.text.secondary, fontSize: typography.fontSize.base, textAlign: 'center', maxWidth: 480, lineHeight: 23 },
  error: { color: colors.error, fontSize: typography.fontSize.sm, marginTop: spacing.xs },
  readOnly: { color: colors.text.secondary, fontSize: typography.fontSize.sm, lineHeight: 21 },
});
